import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, ChevronRight, Save, Trash2, AlertTriangle,
  Eye, EyeOff, Shield, Settings, Code, RefreshCw, CheckCircle2,
  Copy, Check,
} from 'lucide-react'
import clsx from 'clsx'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adApi } from '../../services/adApi'
import { useAdConfigs, useDeleteAdConfig, useUpdateAdConfig } from '../../hooks/useAdDomains'

function Toggle({ label, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div>
        <div className="text-sm font-medium text-gray-800">{label}</div>
        {description && <div className="text-xs text-gray-500 mt-0.5">{description}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={clsx(
          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none',
          checked ? 'bg-blue-600' : 'bg-gray-200'
        )}
      >
        <span
          className={clsx(
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200',
            checked ? 'translate-x-5' : 'translate-x-0'
          )}
        />
      </button>
    </div>
  )
}

function AdminConfirmModal({ onConfirm, onClose, title, description, loading = false }) {
  const { t } = useTranslation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPwd, setShowPwd] = useState(false)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('domains.settings.adminUsernameLabel')}</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('domains.settings.adminUsernamePlaceholder')}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('domains.settings.adminPasswordLabel')}</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('domains.settings.adminPasswordPlaceholder')}
                className="input w-full pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} disabled={loading} className="btn btn-secondary flex-1">{t('common.buttons.cancel')}</button>
          <button
            onClick={() => onConfirm(username, password)}
            disabled={!username || !password || loading}
            className="btn btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
            {t('common.buttons.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function DomainSettings() {
  const { t } = useTranslation()
  const { configId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: configs } = useAdConfigs()
  const domain = configs?.find((c) => c.id === configId)

  const [mode, setMode] = useState('normal') // 'normal' | 'advanced'
  const [showAdminModal, setShowAdminModal] = useState(false)
  const [adminModalMode, setAdminModalMode] = useState('permissions') // 'permissions' | 'sync'
  const [pendingPerms, setPendingPerms] = useState(null)
  const [permMsg, setPermMsg] = useState(null)

  // Sync service account state
  const [syncResult, setSyncResult] = useState(null)
  const [syncMsg, setSyncMsg] = useState(null)
  const [scriptCopied, setScriptCopied] = useState(false)

  const updateMutation = useUpdateAdConfig()
  const deleteMutation = useDeleteAdConfig()

  const { data: permissions, isLoading: permsLoading } = useQuery({
    queryKey: ['ad-permissions', configId],
    queryFn: () => adApi.getPermissions(configId),
    enabled: !!configId,
  })

  const [localPerms, setLocalPerms] = useState(null)
  const currentPerms = localPerms ?? permissions ?? {}

  const updatePermsMutation = useMutation({
    mutationFn: ({ adminUsername, adminPassword }) =>
      adApi.updatePermissions(configId, {
        adminConfirm: { adminUsername, adminPassword },
        permissions: pendingPerms,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ad-permissions', configId] })
      setLocalPerms(null)
      setPendingPerms(null)
      setShowAdminModal(false)
      setPermMsg({ type: 'success', text: t('domains.settings.permsUpdated') })
      setTimeout(() => setPermMsg(null), 4000)
    },
    onError: (e) => {
      setShowAdminModal(false)
      setPermMsg({ type: 'error', text: e.response?.data?.error ?? e.message })
    },
  })

  const syncAccountMutation = useMutation({
    mutationFn: ({ adminUsername, adminPassword }) =>
      adApi.syncServiceAccount(configId, { adminUsername, adminPassword }),
    onSuccess: (data) => {
      setShowAdminModal(false)
      setSyncResult(data)
      setSyncMsg({ type: data.passwordSet ? 'success' : 'warning', text: data.passwordSet ? t('domains.settings.syncSuccess') : t('domains.settings.syncManualPassword') })
      queryClient.invalidateQueries({ queryKey: ['ad-configs'] })
      queryClient.invalidateQueries({ queryKey: ['ad-stats', configId] })
      setTimeout(() => setSyncMsg(null), 6000)
    },
    onError: (e) => {
      setShowAdminModal(false)
      setSyncMsg({ type: 'error', text: e.response?.data?.error ?? e.message })
    },
  })

  const handleSavePerms = (newPerms) => {
    setPendingPerms(newPerms)
    setLocalPerms(newPerms)
    setAdminModalMode('permissions')
    setShowAdminModal(true)
  }

  const handleOpenSyncModal = () => {
    setAdminModalMode('sync')
    setShowAdminModal(true)
  }

  const handleConfirmAdmin = (username, password) => {
    if (adminModalMode === 'sync') {
      syncAccountMutation.mutate({ adminUsername: username, adminPassword: password })
    } else {
      updatePermsMutation.mutate({ adminUsername: username, adminPassword: password })
    }
  }

  const handleCopyScript = (script) => {
    navigator.clipboard.writeText(script).then(() => {
      setScriptCopied(true)
      setTimeout(() => setScriptCopied(false), 2000)
    })
  }

  const handleDelete = async () => {
    if (!window.confirm(t('domains.settings.deleteConfirm', { name: domain?.displayName }))) return
    try {
      await deleteMutation.mutateAsync(configId)
      navigate('/domains')
    } catch (e) {
      alert(t('domains.settings.deleteError') + e.message)
    }
  }

  // Form state para modo avançado
  const [form, setForm] = useState(null)
  const rawForm = form ?? domain ?? {}

  const handleSaveRaw = async (e) => {
    e.preventDefault()
    try {
      await updateMutation.mutateAsync({ id: configId, data: rawForm })
      queryClient.invalidateQueries({ queryKey: ['ad-configs'] })
    } catch (e) {
      alert(t('domains.settings.genericError') + e.message)
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(`/domains/${configId}`)} className="btn btn-secondary text-sm flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" />
          {t('common.buttons.back')}
        </button>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-500 cursor-pointer hover:underline" onClick={() => navigate('/domains')}>{t('domains.title')}</span>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-500 cursor-pointer hover:underline" onClick={() => navigate(`/domains/${configId}`)}>{domain?.displayName}</span>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-800">{t('domains.dashboard.settings')}</span>
      </div>

      {/* Mode toggle */}
      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        <button
          onClick={() => setMode('normal')}
          className={clsx('flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors', mode === 'normal' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700')}
        >
          <Settings className="w-4 h-4" />
          {t('domains.settings.modeNormal')}
        </button>
        <button
          onClick={() => setMode('advanced')}
          className={clsx('flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors', mode === 'advanced' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700')}
        >
          <Code className="w-4 h-4" />
          {t('domains.settings.modeAdvanced')}
        </button>
      </div>

      {mode === 'normal' ? (
        <div className="space-y-6">
          {/* Info visual */}
          <div className="card p-5">
            <h3 className="font-semibold text-gray-900 mb-1">{domain?.displayName}</h3>
            <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
              <div>
                <span className="text-gray-500">{t('domains.settings.fieldServer')}</span>
                <div className="font-mono font-medium text-gray-800">{domain?.server}:{domain?.port}</div>
              </div>
              <div>
                <span className="text-gray-500">{t('domains.settings.fieldSecurity')}</span>
                <div className="font-medium text-gray-800">{domain?.useSsl ? 'LDAPS (SSL)' : 'LDAP'}</div>
              </div>
              <div>
                <span className="text-gray-500">{t('domains.settings.fieldBaseDn')}</span>
                <div className="font-mono font-medium text-gray-800">{domain?.baseDn}</div>
              </div>
              <div>
                <span className="text-gray-500">{t('domains.settings.fieldServiceAccount')}</span>
                <div className="font-mono font-medium text-gray-800 truncate">{domain?.bindDn}</div>
              </div>
            </div>
            <button
              onClick={() => setMode('advanced')}
              className="btn btn-secondary text-sm mt-4 flex items-center gap-1"
            >
              <Code className="w-4 h-4" />
              {t('domains.settings.editRaw')}
            </button>
          </div>

          {/* Conta de Serviço */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5 text-blue-500" />
              <h3 className="font-semibold text-gray-900">{t('domains.settings.serviceAccountTitle')}</h3>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              {t('domains.settings.serviceAccountHint')}
            </p>

            {syncMsg && (
              <div className={clsx('flex items-center gap-2 text-sm p-3 rounded-lg mb-3',
                syncMsg.type === 'success' ? 'bg-green-50 text-green-700'
                : syncMsg.type === 'warning' ? 'bg-yellow-50 text-yellow-800'
                : 'bg-red-50 text-red-700')}>
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                {syncMsg.text}
              </div>
            )}

            {syncResult?.delegationScript && (
              <div className="mb-4 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-gray-700">
                    {syncResult.passwordRequiresManualSet
                      ? t('domains.settings.scriptSetPassword')
                      : t('domains.settings.scriptDelegate')}
                  </p>
                  <button
                    onClick={() => handleCopyScript(syncResult.delegationScript)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    {scriptCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {scriptCopied ? t('domains.settings.scriptCopied') : t('domains.settings.scriptCopy')}
                  </button>
                </div>
                <pre className="text-xs font-mono bg-gray-900 text-green-300 p-3 rounded-xl overflow-x-auto max-h-48 whitespace-pre-wrap">
                  {syncResult.delegationScript}
                </pre>
              </div>
            )}

            <button
              onClick={handleOpenSyncModal}
              disabled={syncAccountMutation.isPending}
              className="btn btn-secondary text-sm flex items-center gap-2"
            >
              {syncAccountMutation.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {t('domains.settings.syncButton')}
            </button>
          </div>

          {/* Permissões sensíveis */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-gray-900">{t('domains.settings.permsTitle')}</h3>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              {t('domains.settings.permsHint')}
            </p>

            {permMsg && (
              <div className={clsx('flex items-center gap-2 text-sm p-3 rounded-lg mb-4', permMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700')}>
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                {permMsg.text}
              </div>
            )}

            {permsLoading ? (
              <div className="text-sm text-gray-400">{t('common.generic.loading')}</div>
            ) : (
              <PermissionsForm
                perms={currentPerms}
                onSave={handleSavePerms}
                saving={updatePermsMutation.isPending}
              />
            )}
          </div>
        </div>
      ) : (
        /* Modo avançado */
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 mb-4">{t('domains.settings.advancedTitle')}</h3>
          <form onSubmit={handleSaveRaw} className="space-y-4">
            {[
              { key: 'displayName',      label: t('domains.settings.rawDisplayName'),   type: 'text'     },
              { key: 'server',           label: t('domains.settings.rawServer'),        type: 'text'     },
              { key: 'ipAddress',        label: t('domains.settings.rawIpAddress'),     type: 'text', hint: t('domains.settings.rawIpAddressHint') },
              { key: 'port',             label: t('domains.settings.rawPort'),          type: 'number'   },
              { key: 'baseDn',           label: t('domains.settings.rawBaseDn'),        type: 'text'     },
              { key: 'bindDn',           label: t('domains.settings.rawBindDn'),        type: 'text'     },
              { key: 'userSearchFilter', label: t('domains.settings.rawUserFilter'),    type: 'text'     },
              { key: 'groupSearchBase',  label: t('domains.settings.rawGroupBase'),     type: 'text'     },
              { key: 'domainAlias',      label: t('domains.settings.rawDomainAlias'),   type: 'text', hint: t('domains.settings.rawDomainAliasHint') },
            ].map(({ key, label, type, hint }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                <input
                  type={type}
                  value={rawForm[key] ?? ''}
                  onChange={(e) => setForm({ ...rawForm, [key]: type === 'number' ? parseInt(e.target.value) : e.target.value })}
                  className="input w-full font-mono text-sm"
                />
                {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
              </div>
            ))}
            <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs text-blue-700 flex items-start gap-2">
              <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                {t('domains.settings.passwordManagedHint1')} <strong>"{t('domains.settings.syncButton')}"</strong> {t('domains.settings.passwordManagedHint2')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="useSsl"
                checked={rawForm.useSsl ?? false}
                onChange={(e) => setForm({ ...rawForm, useSsl: e.target.checked })}
                className="checkbox"
              />
              <label htmlFor="useSsl" className="text-sm text-gray-700">{t('domains.settings.useSsl')}</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enabled"
                checked={rawForm.enabled ?? true}
                onChange={(e) => setForm({ ...rawForm, enabled: e.target.checked })}
                className="checkbox"
              />
              <label htmlFor="enabled" className="text-sm text-gray-700">{t('domains.settings.enabledLabel')}</label>
            </div>
            <div className="flex justify-end pt-2">
              <button type="submit" disabled={updateMutation.isPending} className="btn btn-primary flex items-center gap-2">
                {updateMutation.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('common.buttons.save')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Danger Zone */}
      <div className="card border border-red-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <h3 className="font-semibold text-red-700">{t('domains.settings.dangerTitle')}</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          {t('domains.settings.dangerHint')}
        </p>
        <button
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
          className="btn text-sm flex items-center gap-2 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
        >
          {deleteMutation.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          {t('domains.settings.dangerButton')}
        </button>
      </div>

      {/* Modal confirmação admin */}
      {showAdminModal && (
        <AdminConfirmModal
          title={adminModalMode === 'sync' ? t('domains.settings.syncModalTitle') : t('domains.settings.permsModalTitle')}
          description={adminModalMode === 'sync'
            ? t('domains.settings.syncModalDesc')
            : t('domains.settings.permsModalDesc')}
          onConfirm={handleConfirmAdmin}
          loading={syncAccountMutation.isPending || updatePermsMutation.isPending}
          onClose={() => {
            setShowAdminModal(false)
            setPendingPerms(null)
            setLocalPerms(null)
          }}
        />
      )}
    </div>
  )
}

function PermissionsForm({ perms, onSave, saving }) {
  const { t } = useTranslation()
  const [local, setLocal] = useState({ ...perms })

  const toggle = (key) => setLocal((prev) => ({ ...prev, [key]: !prev[key] }))

  return (
    <div>
      <div className="divide-y divide-gray-100">
        <Toggle label={t('domains.settings.permCreateUser')}  description={t('domains.settings.permCreateUserDesc')}  checked={!!local.createUser}  onChange={() => toggle('createUser')}  />
        <Toggle label={t('domains.settings.permDeleteUser')}  description={t('domains.settings.permDeleteUserDesc')}  checked={!!local.deleteUser}  onChange={() => toggle('deleteUser')}  />
        <Toggle label={t('domains.settings.permEditUser')}    description={t('domains.settings.permEditUserDesc')}    checked={!!local.editUser}    onChange={() => toggle('editUser')}    />
        <Toggle label={t('domains.settings.permCreateOu')}    description={t('domains.settings.permCreateOuDesc')}    checked={!!local.createOu}    onChange={() => toggle('createOu')}    />
        <Toggle label={t('domains.settings.permEditOu')}      description={t('domains.settings.permEditOuDesc')}      checked={!!local.editOu}      onChange={() => toggle('editOu')}      />
        <Toggle label={t('domains.settings.permEditGroup')}   description={t('domains.settings.permEditGroupDesc')}   checked={!!local.editGroup}   onChange={() => toggle('editGroup')}   />
        <Toggle label={t('domains.settings.permDeleteGroup')} description={t('domains.settings.permDeleteGroupDesc')} checked={!!local.deleteGroup} onChange={() => toggle('deleteGroup')} />
      </div>
      <div className="flex justify-end mt-4">
        <button
          onClick={() => onSave(local)}
          disabled={saving}
          className="btn btn-primary text-sm flex items-center gap-2"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
          {t('domains.settings.permsSaveButton')}
        </button>
      </div>
    </div>
  )
}
